<template>
  <FloatingBox
    v-if="visible"
    ref="floating_cont"
    v-click-outside="e => handleClickOutside(e)"
    referenceElId="gmf_vertical"
    :initialPlacement="pinned ? 'custom' : 'reference'"
    :initialReferenceOffset="{ x: -8, y: 8 }"
    :coordinates="coordinates"
    class="floating-gmf-selektor fltbbl anim-fade-in text-noselect"
    @update:coordinates="handleUpdateCoordinates"
  >
    <template #default="v">
      <div
        ref="inner"
        class="fltbbl_inner"
      >
        <div
          class="fltbbl_title f-center cursor-grab"
          @touchstart="e => v.flbox_methods.handleTouchstart(e)"
          @mousedown="e => v.flbox_methods.handleMousedown(e)"
        >
          <div
            class="mdi mdi-drag-vertical pa-1 text-normal-large mr-1 icon_btn csst cursor-grab clr-grey"
            @mouseenter="fl_positionTooltip"
          >
            <div
              class="csst_content clr-white text-nowrap"
              style="position: fixed; line-height: 1.2; max-width: 28rem"
              v-html="$t('tooltips.can_be_dragged_simple')"
            ></div>
          </div>
          <div class="fltbbl_title_text d-flex">
            <div class="mr-2 my-auto">
              {{ $t('common.bubble_factor') }}
            </div>
            <div
              class="mdi mdi-help-circle-outline text-medium my-auto clr-grey csst"
              @mouseenter="e => fl_positionTooltip(e, { offset: 0, placement: 'bottom' })"
            >
              <div
                class="csst_content clr-white csst_content_hoverable text-wrap"
                style="position: fixed; line-height: 1.2; max-width: 28rem"
                v-html="
                  $t('tooltips.bubble_factor_explained', {
                    linkText: !$i18n.g1
                      ? `<a target='_blank' class='weight_700 link-underline' href='${
                          links_url_links.icm_pressure_guide.url
                        }'>${$t('guides.icm_pressure')}</a>`
                      : $t('guides.icm_pressure'),
                    linkTextBubble: !$i18n.g1
                      ? `<div class='mt-2'><a target='_blank' class='weight_700 link-underline' href='${
                          links_url_links.bubble_factor.url
                        }'>
                      ${$t('guides.bubble_factor_learn_more')}</a></div>`
                      : '',
                  })
                "
              ></div>
            </div>
          </div>
          <div class="fltbbl_title_controls ml-auto">
            <Checkbox
              v-for="(item, idx) in controls"
              :key="idx"
              class="csst fltbbl_title_controls_item"
              style="
                --clr-disabled: var(--clr-grey-2);
                --clr-enabled: var(--clr-primary);
                --icon-size: 1.125em;
              "
              :value="item.value"
              :icons="item.icons"
              @click="item.onClick"
              @mouseenter="fl_positionTooltip"
            >
              <template #content>
                <div
                  class="csst_content clr-white"
                  style="position: fixed; line-height: 1.2; max-width: 28rem"
                  v-html="item.tooltip"
                />
              </template>
            </Checkbox>
          </div>
          <div
            class="icon_btn fltbbl_close mdi mdi-close pa-1 text-semi-large csst"
            @click="close"
            @mouseenter="fl_positionTooltip"
          >
            <div
              class="csst_content clr-white text-nowrap"
              style="position: fixed; line-height: 1.2; max-width: 28rem"
            >
              {{ $t('btn.close') }}
            </div>
          </div>
        </div>
        <BubbleFactor
          :gameformat
          :solElite
          style="padding: 1rem"
        />
      </div>
    </template>
  </FloatingBox>
</template>

<script>
import Checkbox from '@/components/Common/Checkbox/Checkbox'
import BubbleFactor from '@/components/BubbleFactor/BubbleFactor'
import FloatingBox from '@/components/lib/FloatingBox'
import float_mix from '@/mixins/float_mix'
import links_mix from '@/mixins/links_mix'
import { SNK } from '@/helpers/helpers'

export default {
  name: 'FloatingBubbleFactor',
  components: {
    BubbleFactor,
    FloatingBox,
    Checkbox,
  },
  mixins: [float_mix, links_mix],
  props: {
    namespace: { type: String, required: true },
  },
  data() {
    return {
      pinned: false,
      coordinates: { percentX: 0, percentY: 0 },
    }
  },
  methods: {
    handleClickOutside(e) {
      this.handleTableClick(e)
    },
    togglePinned() {
      this.pinned = !this.pinned
    },
    toggleKeepHistory() {
      this.gmfKeepHistory = !this.gmfKeepHistory
    },
    handleTableClick() {
      if (!this.pinned) {
        this.close()
      }
    },
    close() {
      this.$store.commit('globals_set', { key: 'floating_bubble_visible', value: false })
    },
    handleUpdateCoordinates({ percentX, percentY }) {
      this.coordinates = { percentX, percentY }
    },
  },
  computed: {
    gameformat() {
      return this.$store.getters[SNK('gameformat', this.namespace)]
    },
    solElite() {
      return this.$store.getters[SNK('sol_elite', this.namespace)]
    },
    isIcm() {
      return (
        (this.solElite?.is_using && this.solElite?.tournamentType === 'icm') ||
        (this.gameformat?.format === 'icm' && !this.solElite?.want_use)
      )
    },
    isEvents() {
      return this.gameformat?.format === 'icm_events'
    },
    visible() {
      return (this.isIcm || this.isEvents) && this.$store.getters.floating_bubble_visible
    },
    controls() {
      return [
        {
          icons: ['mdi-pin-outline', 'mdi-pin'],
          onClick: this.togglePinned,
          value: this.pinned,
          tooltip: this.pinned
            ? this.$t('tooltips.gmf_unpinn_simple')
            : this.$t('tooltips.gmf_pinn_simple'),
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.fltbbl {
  z-index: 8;
  --title-height: 2.5em;
  &_close {
    position: absolute;
    right: 0.5em;
  }
  &_inner {
    border-radius: 4px;
    background: var(--clr-black-1);
    box-shadow:
      0px 0px 0px 1px var(--clr-black-2),
      var(--menu-shadow);
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }
  &_title {
    overflow: hidden;
    border-bottom: solid 1px var(--clr-black-2);
    padding: 0 0.5em;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: var(--title-height);
    line-height: 1.1;
    z-index: 2;
    position: relative;
    &_text {
      width: 100%;
      font-size: 1em;
      font-weight: 600;
      padding: 0 0.5em 0 0;
    }
    &_controls {
      display: flex;
      margin: 0 3rem 0 0.5em;
      &_item {
        &:last-child {
          &:after {
            display: none;
          }
        }
        display: flex;
        align-items: center;
        justify-content: center;
        &:after {
          content: '';
          height: 100%;
          border-right: solid var(--clr-black-2) 1px;
          position: absolute;
          top: 0;
          right: 0;
        }
      }
    }
  }
}
</style>
